import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { Gift, Sparkles, Ticket } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { Seo } from '../../../../components/seo.js';
import { useSpinMutation, useSpinStatus } from '../hooks/use-rewards.js';
import { SpinWheelCard, errorMessage } from '../components/spin-wheel-card.js';
import { RewardsTerms } from '../components/rewards-terms.js';
import type { SpinOutcome } from '../types/rewards.js';

const HIGHLIGHTS = [
  {
    icon: Sparkles,
    title: 'One free spin every day',
    description: 'Come back every 24 hours for another shot at a discount coupon.',
  },
  {
    icon: Ticket,
    title: 'Real coupons, real savings',
    description: 'Winning spins issue a coupon code you can apply straight at checkout.',
  },
  {
    icon: Gift,
    title: 'No purchase required to play',
    description: 'Spinning is free for every signed-in customer -- no order needed to take a spin.',
  },
] as const;

/**
 * Public marketing/acquisition page at `/earn-with-fun` -- explains the
 * spin-to-win rewards program to anyone (signed in or not) and, for
 * signed-in customers, embeds the same spin flow the authenticated
 * `/account/rewards` page uses (same hooks, same `SpinWheelCard`, no
 * duplicated spin logic). Signed-out visitors get a sign-in prompt instead
 * of a login-walled page, since this route is meant to be indexable and
 * link-shareable.
 */
export function EarnWithFunPage() {
  const { data: status, isLoading: isStatusLoading, isError: isStatusError, refetch: refetchStatus } =
    useSpinStatus();
  const spinMutation = useSpinMutation();
  const [outcome, setOutcome] = useState<SpinOutcome | null>(null);
  const [spinError, setSpinError] = useState<string | null>(null);

  const handleSpin = () => {
    if (!status?.canSpin || spinMutation.isPending) return;
    setSpinError(null);
    spinMutation.mutate(undefined, {
      onSuccess: (result) => setOutcome(result.outcome),
      onError: (error) => setSpinError(errorMessage(error, 'Could not spin right now. Please try again.')),
    });
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Seo
        title="Earn with Fun -- Spin & Win Rewards"
        description="Spin the daily rewards wheel for a chance to win discount coupons -- free to play for every signed-in customer."
        canonicalPath={ROUTE_NAMES.earnWithFun}
      />

      <div className="mb-8 text-center">
        <span className="bg-gradient-to-r from-[#FFC800] via-[#FFD84D] to-[#FFC800] mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full">
          <Gift className="h-7 w-7 text-[#1A1A1A]" aria-hidden="true" />
        </span>
        <h1 className="text-2xl font-bold text-gray-900">Earn with Fun</h1>
        <p className="mx-auto mt-2 max-w-xl text-sm text-gray-600">
          Spin our rewards wheel once a day for a chance to win a discount coupon -- no purchase necessary, just
          sign in and give it a spin.
        </p>
      </div>

      <ul className="mb-10 grid gap-4 sm:grid-cols-3">
        {HIGHLIGHTS.map(({ icon: Icon, title, description }) => (
          <li key={title} className="rounded-2xl border border-gray-100 bg-white p-4 text-center">
            <Icon className="mx-auto mb-2 h-6 w-6 text-gray-400" aria-hidden="true" />
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            <p className="mt-1 text-xs text-gray-500">{description}</p>
          </li>
        ))}
      </ul>

      <SignedIn>
        <section aria-labelledby="spin-wheel-heading" className="mb-8">
          <h2 id="spin-wheel-heading" className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Spin the wheel
          </h2>
          <SpinWheelCard
            status={status}
            isStatusLoading={isStatusLoading}
            isStatusError={isStatusError}
            onRetryStatus={() => void refetchStatus()}
            outcome={outcome}
            onSpin={handleSpin}
            onDismissResult={() => setOutcome(null)}
            isSpinning={spinMutation.isPending}
            spinError={spinError}
          />
          <p className="mt-3 text-center text-sm text-gray-500">
            <Link to={ROUTE_NAMES.accountRewards} className="focus-visible:ring-brand font-semibold text-gray-700 underline focus-visible:outline-none focus-visible:ring-2">
              View your coupons and spin history
            </Link>
          </p>
        </section>
      </SignedIn>

      <SignedOut>
        <section
          aria-labelledby="sign-in-heading"
          className="mb-8 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center"
        >
          <Gift className="h-8 w-8 text-gray-300" aria-hidden="true" />
          <h2 id="sign-in-heading" className="text-base font-semibold text-gray-900">
            Sign in to start spinning
          </h2>
          <p className="max-w-sm text-sm text-gray-500">
            Create a free account or sign in to take your daily spin and start collecting reward coupons.
          </p>
          <Link
            to={ROUTE_NAMES.signIn}
            className="bg-brand text-brand-foreground hover:bg-brand/90 focus-visible:ring-brand rounded-full px-5 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2"
          >
            Sign in
          </Link>
        </section>
      </SignedOut>

      <RewardsTerms />
    </div>
  );
}
